import React, { Component } from 'react';
import { withStyles } from '@mui/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationItem from '@mui/material/BottomNavigationAction';
import { grey } from '@mui/material/colors';
import polygonSvgEdit from '../../../style/images/drawing/edit_location.svg';
import deleteLocationMarker from '../../../style/images/drawing/delete-location.svg';
import TimelineIcon from '@mui/icons-material/Timeline';
import StraightenIcon from '@mui/icons-material/Straighten';
import { DRAWING_MODES, PolygonDrawingContext } from './PolygonDrawingContext';
import { getLat, getLng, toGoogleLatLng } from './PolygonDrawingUtils';

const styles = (theme) => ({
  root: {
    zIndex: 10,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.white,
  },

  nav: {
    width: 'auto',
  },

  button: {
    margin: [[0, 20]],
    pointerEvents: 'all',
    '&:hover:not(.disabled)': {
      backgroundColor: grey['200'],
    },
    '&.disabled': {
      cursor: 'not-allowed',
    },
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '& svg': {
        color: theme.palette.primary.contrastText,
      },
    },
    '&.animation': {
      animation: '$borderFlash 1s infinite',
      boxShadow: '0 0 0 2px rgba(255, 193, 7, 0.2) inset ',
    },
  },

  label: {
    whiteSpace: 'nowrap',
  },

  '@keyframes borderFlash': {
    '0%': {
      boxShadow: '0 0 0 2px rgba(255, 193, 7, 0.2) inset',
    },
    '50%': {
      boxShadow: '0 0 0 2px rgba(255, 193, 7, 1) inset',
    },
    '100%': {
      boxShadow: '0 0 0 2px rgba(255, 193, 7, 0.2) inset',
    },
  },
});

class PolygonToolbar extends Component {
  static contextType = PolygonDrawingContext;
  state = {
    hoveredIndex: null,
  };

  handleMouseEnter = (index, active) => {
    if (active) {
      this.onMouseEnter();
      this.setState({ hoveredIndex: index });
    }
  };

  handleMouseLeave = () => {
    this.onMouseLeave();
    this.setState({ hoveredIndex: null });
  };

  deleteHandler = () => {
    const { dispatch } = this.context;
    const {
      actions,
      acIndex,
      polygons,
      selectedMarker,
      selectedMeasurementLine,
      measurementMarkers,
      markers,
    } = this.context.state;

    const cloneLatLng = (pt) => toGoogleLatLng(pt);

    const trimmedActions = acIndex !== -1 ? actions.slice(0, acIndex + 1) : [...actions];

    // --- Handle measurement line deletion ---
    if (selectedMeasurementLine) {
      const startIndex = selectedMeasurementLine.index;
      const newMarkers = measurementMarkers.filter(
        (_, i) => i !== startIndex && i !== startIndex + 1
      );

      const newAction = {
        path: markers.map((m) => ({ ...m })),
        measurementMarkers: newMarkers.map((m) => ({ ...m })),
        polygons: polygons.map((p) => ({
          ...p,
          path: p.path ? p.path.map(cloneLatLng) : [],
        })),
      };

      const newActions = [...trimmedActions, [newAction]];

      dispatch({
        type: 'DELETE_SELECTED_MEASUREMENT_LINE',
        payload: {
          measurementMarkers: newMarkers,
          actions: newActions,
          acIndex: newActions.length - 1,
        },
      });

      return;
    }

    if (!selectedMarker?.position) return;

    const selectedLat = getLat(selectedMarker.position);
    const selectedLng = getLng(selectedMarker.position);

    // --- Handle polygon marker deletion ---
    let selectedPolygonIndex = -1;
    polygons.forEach((polygon, idx) => {
      polygon.path.forEach((pt) => {
        if (getLat(pt) === selectedLat && getLng(pt) === selectedLng) {
          selectedPolygonIndex = idx;
        }
      });
    });

    if (selectedPolygonIndex === -1) return;

    const newPolygons = polygons.map((poly, idx) => {
      if (idx !== selectedPolygonIndex) {
        return {
          ...poly,
          path: poly.path.map(cloneLatLng),
        };
      }

      const newPath = poly.path
        .filter((pt) => !(getLat(pt) === selectedLat && getLng(pt) === selectedLng))
        .map(cloneLatLng);

      if (newPath.length < 3) return poly;

      return { ...poly, path: newPath };
    });

    const newAction = {
      path: markers.map((m) => ({ ...m })),
      measurementMarkers: measurementMarkers.map((m) => ({ ...m })),
      polygons: newPolygons.map((p) => ({
        ...p,
        path: p.path ? [...p.path] : [],
      })),
    };

    const newActions = [...trimmedActions, [newAction]];

    dispatch({
      type: 'DELETE_SELECTED_MARKER',
      payload: {
        polygons: newPolygons,
        actions: newActions,
        acIndex: newActions.length - 1,
        selectedMarker: null,
      },
    });
  };

  undoHandler = () => {
    const { dispatch } = this.context;
    const { actions, acIndex, measuring } = this.context.state;
    const { drawPolyline, measureHandler, toggleDraw, markerListener, isMagicSelecting, polyline } =
      this.props;

    const newAcIndex = acIndex - 1;
    const action = actions[newAcIndex]?.[0];

    if (!action) {
      dispatch({ type: 'SET_MARKERS', payload: [] });
      dispatch({ type: 'SET_POLYGONS', payload: { polygons: [], polygonVisible: false } });
      dispatch({ type: 'SET_MEASUREMENT_MARKERS', payload: [] });

      if (polyline) {
        polyline.setMap(null);
      }

      if (measuring) {
        measureHandler();
      }

      dispatch({
        type: 'UNDO',
        payload: {
          acIndex: newAcIndex,
          actions: [...actions],
          polygons: [],
        },
      });

      return;
    }

    const { path = [], measurementMarkers = [], polygons = [] } = action;

    if (isMagicSelecting) {
      const polygon = [{ valid: true, path: path }];
      dispatch({
        type: 'MAGIC_SELECTION',
        payload: { polygons: polygon, actions: [...actions], acIndex: newAcIndex },
      });
      return;
    }

    dispatch({ type: 'SET_MARKERS', payload: path });
    dispatch({
      type: 'SET_POLYGONS',
      payload: { polygons: polygons, polygonVisible: polygons.length > 0 },
    });
    dispatch({ type: 'SET_MEASUREMENT_MARKERS', payload: measurementMarkers });

    if (measurementMarkers.length % 2 !== 0 || measuring) {
      measureHandler();
    } else if (
      (polygons.length === 0 && path.length > 0 && markerListener?.instance == null) ||
      (path.length === 0 && markerListener?.instance != null)
    ) {
      toggleDraw();
    }

    if (polygons.length < 1) {
      drawPolyline(path);
    } else {
      polyline.setMap(null);
    }

    dispatch({
      type: 'UNDO',
      payload: {
        acIndex: newAcIndex,
        actions: [...actions],
        polygons: polygons,
      },
    });
  };

  redoHandler = () => {
    const { dispatch } = this.context;
    const { actions, acIndex, measuring, polygonVisible } = this.context.state;
    const { drawPolyline, measureHandler, markerListener, toggleDraw, isMagicSelecting, polyline } =
      this.props;

    const newAcIndex = acIndex + 1;
    const action = actions[newAcIndex]?.[0];

    const { path = [], measurementMarkers = [], polygons = [] } = action;

    if (isMagicSelecting) {
      const polygon = [{ valid: true, path: path }];
      dispatch({
        type: 'MAGIC_SELECTION',
        payload: { polygons: polygon, actions: [...actions], acIndex: newAcIndex },
      });
      return;
    }

    dispatch({ type: 'SET_MARKERS', payload: path });
    dispatch({ type: 'SET_MEASUREMENT_MARKERS', payload: measurementMarkers });
    dispatch({
      type: 'SET_POLYGONS',
      payload: { polygons: polygons, polygonVisible: polygons.length > 0 },
    });

    if (
      (measurementMarkers.length % 2 === 0 && measuring) ||
      (measurementMarkers.length % 2 !== 0 && !measuring)
    ) {
      measureHandler();
    } else if (
      (polygons.length === 0 && markerListener?.instance == null) ||
      (!polygonVisible && polygons.length > 0) ||
      (path.length === 0 && markerListener?.instance != null)
    ) {
      toggleDraw();
    }

    if (polygons.length < 1) {
      drawPolyline(path);
    } else {
      polyline.setMap(null);
    }

    dispatch({
      type: 'REDO',
      payload: {
        acIndex: newAcIndex,
        actions: [...actions],
        polygons: polygons,
      },
    });
  };

  onMouseEnter = () => {
    const { selectedMarker, selectedMeasurementLine } = this.context.state;
    if (selectedMarker) {
      this.resizeIcon(2, selectedMarker);
    } else if (selectedMeasurementLine) {
      this.resizeIcon(2, selectedMeasurementLine);
    }
  };

  onMouseLeave = () => {
    const { selectedMarker, selectedMeasurementLine } = this.context.state;
    if (selectedMarker) {
      this.resizeIcon(1, selectedMarker);
    } else if (selectedMeasurementLine) {
      this.resizeIcon(1, selectedMeasurementLine);
    }
  };

  resizeIcon = (scale, element) => {
    const size = 24 * scale;

    element.icon = {
      url: scale === 1 ? polygonSvgEdit : deleteLocationMarker,
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size),
    };

    const { dispatch } = this.context;
    if (element instanceof google.maps.Polyline) {
      dispatch({ type: 'SET_SELECTED_MEASUREMENT_LINE', payload: element });
    } else {
      dispatch({ type: 'SET_SELECTED_MARKER', payload: element });
    }
  };

  render() {
    const {
      classes,
      LangFile,
      isMagicSelecting,
      resetHandler,
      toggleDraw,
      measureHandler,
      markerListener,
    } = this.props;

    const {
      drawingMode,
      acIndex,
      selectedMarker,
      polygons,
      actions,
      markers,
      measurementMarkers,
      playAnimation,
      measuring,
      selectedMeasurementLine,
    } = this.context.state;

    const undoIsActive =
      drawingMode === DRAWING_MODES.EDIT_FIELD_BOUNDARIES ||
      drawingMode === DRAWING_MODES.SPLIT_FIELD_POLYGONS ||
      drawingMode === DRAWING_MODES.EDIT_MERGED_FIELD
        ? acIndex > 0
        : acIndex > -1;

    const redoIsActive = acIndex + 1 < actions.length;
    const deleteIsActive =
      (polygons[0] && polygons[0].path.length > 3 && selectedMarker) || selectedMeasurementLine;
    const resetIsActive = undoIsActive || redoIsActive;

    const disableMeasurementTool =
      (markers && markers.length > 0 && markerListener?.instance != null) || isMagicSelecting;
    const disableDrawingTool =
      (measurementMarkers && measurementMarkers.length % 2 !== 0) ||
      polygons.length !== 0 ||
      isMagicSelecting;

    const isDrawingMarkers = !measuring && markerListener?.instance != null;
    const isDrawingMeasurement = measuring && markerListener?.instance != null;

    return (
      <div className={classes.root}>
        <BottomNavigation className={classes.nav} value={this.state.hoveredIndex} showLabels>
          <BottomNavigationItem
            onClick={() => {
              this.deleteHandler();
              if (!deleteIsActive) {
                this.handleMouseLeave();
              }
            }}
            icon={<DeleteIcon color={deleteIsActive ? 'primary' : 'inherit'} />}
            classes={{
              label: classes.label,
              root: `${classes.button} ${!deleteIsActive ? 'disabled' : ''}`,
            }}
            label={LangFile.PolygonToolbar.deleteButton}
            onMouseEnter={() => this.handleMouseEnter(0, deleteIsActive)}
            onMouseLeave={() => this.handleMouseLeave(deleteIsActive)}
            disabled={!deleteIsActive}
          />

          <BottomNavigationItem
            onClick={() => {
              this.undoHandler();
              if (!undoIsActive) {
                this.handleMouseLeave();
              }
            }}
            icon={<UndoIcon color={undoIsActive ? 'primary' : 'inherit'} />}
            label={LangFile.PolygonToolbar.undoButton}
            classes={{
              label: classes.label,
              root: `${classes.button} ${!undoIsActive ? 'disabled' : ''}`,
            }}
            onMouseEnter={() => this.handleMouseEnter(1, undoIsActive)}
            onMouseLeave={() => this.handleMouseLeave()}
            disabled={!undoIsActive}
          />

          <BottomNavigationItem
            onClick={() => {
              this.redoHandler();
              if (!redoIsActive) {
                this.handleMouseLeave();
              }
            }}
            icon={<RedoIcon color={redoIsActive ? 'primary' : 'inherit'} />}
            label={LangFile.PolygonToolbar.redoButton}
            classes={{
              label: classes.label,
              root: `${classes.button} ${!redoIsActive ? 'disabled' : ''}`,
            }}
            onMouseEnter={() => this.handleMouseEnter(2, redoIsActive)}
            onMouseLeave={() => this.handleMouseLeave()}
            disabled={!redoIsActive}
          />

          <BottomNavigationItem
            onClick={() => {
              resetHandler();
              if (!resetIsActive) {
                this.handleMouseLeave();
              }
            }}
            icon={<HistoryIcon color={resetIsActive ? 'primary' : 'inherit'} />}
            classes={{
              label: classes.label,
              root: `${classes.button} ${!resetIsActive ? 'disabled' : ''}`,
            }}
            label={LangFile.PolygonToolbar.resetButton}
            onMouseEnter={() => this.handleMouseEnter(3, resetIsActive)}
            onMouseLeave={() => this.handleMouseLeave()}
            disabled={!resetIsActive}
          />

          <BottomNavigationItem
            onClick={() => {
              toggleDraw();
            }}
            icon={<TimelineIcon color={disableDrawingTool ? 'inherit' : 'primary'} />}
            classes={{
              label: classes.label,
              root: `${classes.button} ${disableDrawingTool ? 'disabled' : ''}  ${isDrawingMarkers ? 'active' : ''} ${playAnimation && !disableDrawingTool ? 'animation' : ''}`,
            }}
            label={LangFile.PolygonToolbar.drawButton}
            disabled={disableDrawingTool}
          />

          <BottomNavigationItem
            onClick={() => {
              measureHandler();
            }}
            icon={<StraightenIcon color={disableMeasurementTool ? 'inherit' : 'primary'} />}
            classes={{
              label: classes.label,
              root: `${classes.button} ${disableMeasurementTool ? 'disabled' : ''} ${isDrawingMeasurement ? 'active' : ''} ${playAnimation && !disableMeasurementTool ? 'animation' : ''}`,
            }}
            label={LangFile.PolygonToolbar.measureButton}
            disabled={disableMeasurementTool}
          />
        </BottomNavigation>
      </div>
    );
  }
}

export default withStyles(styles)(PolygonToolbar);
